import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorOwner, QueueLogMessageDto } from '../../api/api';
import { InterpretationNote } from './InterpretationNote';
import './Interpretation.scss';

/** The code of the group of rows no rule recognised. */
export const UNRECOGNISED = 'unrecognised';

const OWNER_ORDER: Record<ErrorOwner, number> = {
    [ErrorOwner.Customer]: 0,
    [ErrorOwner.Support]: 1,
    [ErrorOwner.Automatic]: 2,
    [ErrorOwner.None]: 3,
};

/** Rows whose interpretation says nobody has to do anything any more. */
export const isSettled = (row: QueueLogMessageDto): boolean =>
    row.interpretation?.owner === ErrorOwner.None;

/**
 * The kind of problem a row belongs to. An error no rule recognises is grouped with the ones
 * that say the same with other ids, like the issue backlog does, instead of all of them in
 * one bucket that hides whether it is one new problem or twenty.
 */
export const codeOf = (row: QueueLogMessageDto): string =>
    row.interpretation?.code ?? row.fingerprint ?? UNRECOGNISED;

/** "3 InvoiceSalesEntry · 1 Customer": which kinds of object a group is about. */
const objectTypes = (rows: QueueLogMessageDto[]): string => {
    const counts = new Map<string, number>();

    rows.forEach((row) => {
        const type = String(row.objectType ?? 'Onbekend');
        counts.set(type, (counts.get(type) ?? 0) + 1);
    });

    return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => `${count} ${type}`)
        .join(' · ');
};

interface IGroup {
    code: string;
    rows: QueueLogMessageDto[];
}

export interface IErrorSummaryProps {
    /** Every row of the period, before the summary's own filters. */
    rows: QueueLogMessageDto[];

    hideSettled: boolean;
    onHideSettledChange: (hide: boolean) => void;

    /** The kind of error the list is narrowed to, or null for all. */
    activeCode: string | null;
    onActiveCodeChange: (code: string | null) => void;

    /** Where the backlog of this environment is, for what this list does not show. */
    issuesLink?: string;
}

/**
 * What is going on in the error log, per kind of problem: how many objects, who has to act,
 * and what to do.
 *
 * The list underneath answers "which objects". This answers the question that comes first
 * when a customer says the log keeps filling up: is it one problem a hundred times or a
 * hundred problems, and whose move is it.
 */
export const ErrorSummary = ({
    rows,
    hideSettled,
    onHideSettledChange,
    activeCode,
    onActiveCodeChange,
    issuesLink,
}: IErrorSummaryProps) => {
    const [expanded, setExpanded] = useState<string | null>(null);

    const groups = useMemo<IGroup[]>(() => {
        const byCode = new Map<string, QueueLogMessageDto[]>();

        rows.forEach((row) => {
            const code = codeOf(row);
            byCode.set(code, [...(byCode.get(code) ?? []), row]);
        });

        return Array.from(byCode.entries())
            .map(([code, grouped]) => ({ code, rows: grouped }))
            .sort((a, b) => {
                const ownerA = a.rows[0].interpretation?.owner;
                const ownerB = b.rows[0].interpretation?.owner;
                const orderA = ownerA ? OWNER_ORDER[ownerA] : 1.5;
                const orderB = ownerB ? OWNER_ORDER[ownerB] : 1.5;

                return orderA - orderB || b.rows.length - a.rows.length;
            });
    }, [rows]);

    const settledCount = useMemo(() => rows.filter(isSettled).length, [rows]);

    if (rows.length === 0) {
        return null;
    }

    const shown = groups.filter(
        (group) => !hideSettled || !group.rows.every((row) => isSettled(row))
    );

    return (
        <section className="error-summary" aria-label="Overzicht van de fouten">
            <div className="error-summary__head">
                <h3 className="error-summary__title">
                    Wat er speelt: {shown.length}{' '}
                    {shown.length === 1 ? 'soort fout' : 'soorten fouten'}
                </h3>

                <label className="error-summary__toggle">
                    <input
                        type="checkbox"
                        checked={hideSettled}
                        onChange={(e) => onHideSettledChange(e.target.checked)}
                    />
                    Afgehandelde regels verbergen ({settledCount})
                </label>
            </div>

            <ul className="error-summary__list">
                {shown.map((group) => {
                    const interpretation = group.rows[0].interpretation;
                    const active = activeCode === group.code;
                    const open = expanded === group.code;

                    return (
                        <li
                            key={group.code}
                            className={`error-summary__item${
                                active ? ' error-summary__item--active' : ''
                            }`}
                        >
                            <div className="error-summary__row">
                                <button
                                    type="button"
                                    className="error-summary__expand"
                                    onClick={() =>
                                        setExpanded(open ? null : group.code)
                                    }
                                    aria-expanded={open}
                                    aria-label={
                                        open ? 'Inklappen' : 'Uitleg tonen'
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={
                                            open ? 'angle-down' : 'angle-right'
                                        }
                                    />
                                </button>

                                {interpretation ? (
                                    <InterpretationNote
                                        interpretation={interpretation}
                                        compact
                                    />
                                ) : (
                                    <span
                                        className="interpretation__title"
                                        title="Geen regel herkent deze fout nog. Op de pagina Issues staat hij bij de onbekende fouten, om er een regel voor te laten schrijven."
                                    >
                                        Onbekende fout:{' '}
                                        <code>
                                            {group.rows[0].fingerprintPattern ??
                                                'zie de melding per regel'}
                                        </code>
                                    </span>
                                )}

                                <span
                                    className="error-summary__count"
                                    title={objectTypes(group.rows)}
                                >
                                    {group.rows.length}{' '}
                                    {group.rows.length === 1
                                        ? 'object'
                                        : 'objecten'}
                                    <small className="d-block text-muted">
                                        {objectTypes(group.rows)}
                                    </small>
                                </span>

                                <button
                                    type="button"
                                    className={`btn btn-sm ${
                                        active
                                            ? 'btn-primary'
                                            : 'btn-outline-secondary'
                                    }`}
                                    onClick={() =>
                                        onActiveCodeChange(
                                            active ? null : group.code
                                        )
                                    }
                                >
                                    {active ? 'Alles tonen' : 'Toon in lijst'}
                                </button>
                            </div>

                            {open && interpretation && (
                                <div className="error-summary__detail">
                                    <InterpretationNote
                                        interpretation={interpretation}
                                        bodyOnly
                                    />
                                </div>
                            )}

                            {open && !interpretation && (
                                <div className="error-summary__detail">
                                    <p className="error-summary__note">
                                        Geen regel herkent deze fout nog, dus er staat
                                        geen uitleg bij. Zo luidt hij, zoals de connector
                                        hem schreef:
                                    </p>
                                    <pre className="error-summary__raw">
                                        {group.rows[0].errorText ??
                                            group.rows[0].errorMessage ??
                                            ''}
                                    </pre>
                                    {group.rows[0].errorLocation && (
                                        <p className="error-summary__note">
                                            Plek in de code:{' '}
                                            <code>{group.rows[0].errorLocation}</code>
                                        </p>
                                    )}
                                    {issuesLink && (
                                        <p className="error-summary__note">
                                            Op de pagina Issues, tabblad{' '}
                                            <Link to="/issues?tab=onbekend">
                                                Onbekende fouten
                                            </Link>
                                            , staat hij met wat nodig is om er een
                                            regel voor te laten schrijven.
                                        </p>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            {hideSettled && settledCount > 0 && (
                <p className="error-summary__note">
                    {settledCount} regel(s) verborgen: het object staat
                    inmiddels in Exact, of is bewust afgehandeld, zoals een
                    verwerkte stornering.
                </p>
            )}

            {issuesLink && (
                <p className="error-summary__note">
                    Dit logboek laat alleen zien wat nu nog niet in Exact staat.
                    Wat in deze periode misging en inmiddels is opgelost, en wat
                    al langer openstaat, staat in de{' '}
                    <Link to={issuesLink}>issues van deze omgeving</Link>.
                </p>
            )}
        </section>
    );
};
